import * as React from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import LoadingButton from "@mui/lab/LoadingButton";
import { useNavigate } from "react-router-dom";
import PageShell from "./shared/PageShell";
import { pageStyles } from "./shared/theme";

const PASSWORD_PATTERN =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d])[\s\S]{8,}$/;

export default function ResetPassword() {
  const urlParams = new URLSearchParams(window.location.search);
  const token = urlParams.get("token");
  const externalId = urlParams.get("id");
  const navigate = useNavigate();

  const [passwordError, setPasswordError] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [showPassword, setShowPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const newPassword = data.get("new_password");
    const confirmPassword = data.get("confirm_password");

    if (!PASSWORD_PATTERN.test(newPassword)) {
      setPasswordError(
        "Use at least 8 characters, including an uppercase letter, lowercase letter, number, and special character."
      );
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError("Passwords do not match.");
      return;
    }

    try {
      setLoading(true);
      setPasswordError("");
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/v1/auth/reset-password`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            reset_token: token,
            new_password: newPassword,
            external_user_id: externalId,
          }),
        }
      );
      if (!response.ok) throw new Error("Unable to reset password");
      navigate("/accounts/passwordreset/success");
    } catch (_error) {
      setPasswordError(
        "We couldn't update your password. The reset link may have expired."
      );
    } finally {
      setLoading(false);
    }
  };

  const visibilityButton = (visible, toggle, label) => (
    <InputAdornment position="end">
      <IconButton
        aria-label={label}
        onClick={toggle}
        onMouseDown={(event) => event.preventDefault()}
        edge="end"
      >
        {visible ? <VisibilityOff /> : <Visibility />}
      </IconButton>
    </InputAdornment>
  );

  return (
    <PageShell>
      <Typography component="h1" variant="h1" sx={pageStyles.title}>
        Create a new password
      </Typography>
      <Typography variant="body1" sx={pageStyles.subtitle}>
        Choose a secure password for your Finn account.
      </Typography>

      {passwordError ? (
        <Alert severity="error" sx={{ mb: 2.5 }}>
          {passwordError}
        </Alert>
      ) : null}

      <Box component="form" onSubmit={handleSubmit} noValidate>
        <TextField
          fullWidth
          id="new_password"
          label="New password"
          name="new_password"
          autoComplete="new-password"
          onChange={() => setPasswordError("")}
          type={showPassword ? "text" : "password"}
          sx={pageStyles.field}
          InputProps={{
            endAdornment: visibilityButton(
              showPassword,
              () => setShowPassword((current) => !current),
              showPassword ? "Hide password" : "Show password"
            ),
          }}
        />
        <TextField
          fullWidth
          id="confirm_password"
          label="Confirm password"
          name="confirm_password"
          autoComplete="new-password"
          onChange={() => setPasswordError("")}
          type={showConfirmPassword ? "text" : "password"}
          sx={pageStyles.field}
          InputProps={{
            endAdornment: visibilityButton(
              showConfirmPassword,
              () => setShowConfirmPassword((current) => !current),
              showConfirmPassword ? "Hide confirmation" : "Show confirmation"
            ),
          }}
        />
        <Typography variant="body2" sx={{ color: "#61768A", mt: -1, mb: 2.5 }}>
          At least 8 characters with uppercase, lowercase, number, and special
          character.
        </Typography>
        <LoadingButton
          type="submit"
          loading={loading}
          fullWidth
          variant="contained"
          sx={pageStyles.primaryButton}
        >
          Save password
        </LoadingButton>
      </Box>
    </PageShell>
  );
}
